import { useState, useEffect, useCallback } from 'react';
import {
  Layout,
  Card,
  FormLayout,
  TextField,
  Button,
  Banner,
  Text,
  Divider,
  BlockStack,
  InlineStack,
  Badge,
} from '@shopify/polaris';
import { apiGet, apiPut } from '../api';

interface PointsConfigData {
  moodCheckin: number;
  recipeViewed: number;
  meditationDone: number;
  matchaCheckin: number;
  journal: number;
  dailyBonus: number;
  maxDailyRitual: number;
  streak5: number;
  streak10: number;
  streak30: number;
  signup: number;
  completeProfile: number;
  programComplete: number;
  subscriptionActivated: number;
  paymentMethodAdded: number;
  purchasePerEuro: number;
  review: number;
  ugcPhoto: number;
  maxDailyUgc: number;
  referralRegister: number;
  referralPurchase: number;
  referralWelcome: number;
  announcementReaction: number;
  announcementVote: number;
}

type FieldKey = keyof PointsConfigData;

interface FieldDef {
  key: FieldKey;
  label: string;
  helpText?: string;
}

const SECTIONS: Array<{ title: string; description: string; fields: FieldDef[] }> = [
  {
    title: 'Ritual diario',
    description: 'Puntos por cada paso del ritual y bonus diario',
    fields: [
      { key: 'moodCheckin', label: 'Check-in de ánimo', helpText: 'Puntos al registrar estado de ánimo' },
      { key: 'recipeViewed', label: 'Receta vista', helpText: 'Puntos al ver la receta del día' },
      { key: 'meditationDone', label: 'Meditación completada', helpText: 'Puntos al completar meditación' },
      { key: 'matchaCheckin', label: 'Matcha check-in', helpText: 'Puntos al registrar matcha del día' },
      { key: 'journal', label: 'Diario', helpText: 'Puntos al escribir en el diario' },
      { key: 'dailyBonus', label: 'Bonus diario (3/3 pasos)', helpText: 'Bonus al completar los 3 pasos core' },
      { key: 'maxDailyRitual', label: 'Máximo diario ritual', helpText: 'Límite anti-abuso de puntos por ritual al día' },
    ],
  },
  {
    title: 'Rachas',
    description: 'Bonificaciones por rachas consecutivas (ciclo de 30 días)',
    fields: [
      { key: 'streak5', label: 'Racha 5 días' },
      { key: 'streak10', label: 'Racha 10 días' },
      { key: 'streak30', label: 'Racha 30 días' },
    ],
  },
  {
    title: 'Acciones únicas',
    description: 'Puntos otorgados una sola vez',
    fields: [
      { key: 'signup', label: 'Registro' },
      { key: 'completeProfile', label: 'Completar perfil' },
      { key: 'programComplete', label: 'Programa completado' },
      { key: 'subscriptionActivated', label: 'Suscripción activada' },
      { key: 'paymentMethodAdded', label: 'Método de pago añadido', helpText: 'Puntos al guardar tarjeta por primera vez' },
    ],
  },
  {
    title: 'Compras y referidos',
    description: 'Puntos por compras, reseñas y programa de referidos',
    fields: [
      { key: 'purchasePerEuro', label: 'Puntos por euro de compra' },
      { key: 'review', label: 'Reseña de producto' },
      { key: 'ugcPhoto', label: 'Foto UGC' },
      { key: 'maxDailyUgc', label: 'Máximo diario UGC' },
      { key: 'referralRegister', label: 'Referido → quien refiere (registro)' },
      { key: 'referralPurchase', label: 'Referido → quien refiere (compra)' },
      { key: 'referralWelcome', label: 'Referido → bienvenida al nuevo' },
    ],
  },
  {
    title: 'Comunicados',
    description: 'Puntos por interacción con comunicados',
    fields: [
      { key: 'announcementReaction', label: 'Reacción a comunicado' },
      { key: 'announcementVote', label: 'Voto en encuesta' },
    ],
  },
];

export default function PointsConfigContent() {
  const [config, setConfig] = useState<PointsConfigData | null>(null);
  const [defaults, setDefaults] = useState<PointsConfigData | null>(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiGet<{ config: PointsConfigData; defaults: PointsConfigData }>('/panel-api/points-config');
      setConfig(data.config);
      setDefaults(data.defaults);
    } catch (err) {
      setError('Error cargando configuración');
    }
  }, []);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  const handleChange = (key: FieldKey, value: string) => {
    if (!config) return;
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    setConfig({ ...config, [key]: num });
    setDirty(true);
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const data = await apiPut<{ config: PointsConfigData }>('/panel-api/points-config', config);
      setConfig(data.config);
      setDirty(false);
      setSuccess(true);
    } catch (err) {
      setError('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (defaults) {
      setConfig({ ...defaults });
      setDirty(true);
      setSuccess(false);
    }
  };

  const isModified = (key: FieldKey): boolean => {
    if (!config || !defaults) return false;
    return config[key] !== defaults[key];
  };

  if (!config) {
    return (
      <Layout>
        <Layout.Section>
          <Card>
            <Text as="p" variant="bodyMd">Cargando configuración…</Text>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <Layout>
      <Layout.Section>
        <InlineStack align="end" gap="200">
          <Button
            tone="critical"
            onClick={handleReset}
          >
            Restaurar valores por defecto
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={saving}
            disabled={!dirty}
          >
            Guardar
          </Button>
        </InlineStack>
      </Layout.Section>

      {success && (
        <Layout.Section>
          <Banner tone="success" onDismiss={() => setSuccess(false)}>
            Configuración guardada correctamente.
          </Banner>
        </Layout.Section>
      )}
      {error && (
        <Layout.Section>
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Layout.Section>
      )}

      {SECTIONS.map((section, idx) => (
        <Layout.AnnotatedSection
          key={idx}
          title={section.title}
          description={section.description}
        >
          <Card>
            <BlockStack gap="400">
              <FormLayout>
                {section.fields.map((field) => (
                  <InlineStack key={field.key} gap="200" align="center" blockAlign="center">
                    <div style={{ flex: 1 }}>
                      <TextField
                        label={field.label}
                        type="number"
                        value={String(config[field.key])}
                        onChange={(v) => handleChange(field.key, v)}
                        helpText={field.helpText}
                        min={0}
                        autoComplete="off"
                        suffix="pts"
                      />
                    </div>
                    {isModified(field.key) && (
                      <Badge tone="attention">Modificado</Badge>
                    )}
                  </InlineStack>
                ))}
              </FormLayout>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>
      ))}

      <Layout.Section>
        <Card>
          <BlockStack gap="300">
            <Text as="h3" variant="headingMd">Proyección mensual (usuario activo)</Text>
            <Divider />
            <BlockStack gap="200">
              {(() => {
                const ritualDaily = config.moodCheckin + config.recipeViewed + config.meditationDone + config.matchaCheckin + config.journal + config.dailyBonus;
                const ritualMonthly = Math.min(ritualDaily, config.maxDailyRitual) * 30;
                const streaksMonthly = config.streak5 + config.streak10 + config.streak30;
                const total = ritualMonthly + streaksMonthly;
                return (
                  <>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd">Ritual diario × 30</Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">{ritualMonthly} pts ({(ritualMonthly / 100).toFixed(2)}€)</Text>
                    </InlineStack>
                    <InlineStack align="space-between">
                      <Text as="span" variant="bodyMd">Rachas (5+10+30)</Text>
                      <Text as="span" variant="bodyMd" fontWeight="semibold">{streaksMonthly} pts ({(streaksMonthly / 100).toFixed(2)}€)</Text>
                    </InlineStack>
                    <Divider />
                    <InlineStack align="space-between">
                      <Text as="span" variant="headingSm">Total estimado / mes</Text>
                      <Text as="span" variant="headingSm">{total} pts ({(total / 100).toFixed(2)}€)</Text>
                    </InlineStack>
                  </>
                );
              })()}
            </BlockStack>
          </BlockStack>
        </Card>
      </Layout.Section>
    </Layout>
  );
}
